import { useState, useEffect, useRef } from 'react';
import {
  updateCoachProfile,
  updatePlayerProfile,
  uploadCoachAvatar,
  uploadPlayerAvatar,
} from '../services/userProfile';
import { getCoachOfferings, updateCoachOffering } from '../services/coachOfferings';
import { COACH_SPECIALTIES } from '../types/user';
import type { CoachSpecialty, SkillLevel } from '../types/user';
import type { AdminUserData } from '../services/admin';
import { useToast } from '../hooks/useToast';
import { MAX_IMAGE_SIZE, getMaxImageSizeLabel } from '../config/constants';
import RichTextEditor from './RichTextEditor';

const MAX_BIO = 1200;

const CERTIFICATIONS = ['ATP Coach', 'WTA Coach', 'NCAA Coach', 'USTA PTR', 'USPTA', 'RPTA'];
const PLAYING_LEVELS = ['ATP Career High', 'WTA Career High', 'ITF Career High', 'NCAA D1', 'NCAA All-American', 'NCAA National Champion'];
const SKILL_LEVELS: SkillLevel[] = ['beginner', 'intermediate', 'advanced', 'professional'];

interface Props {
  user: AdminUserData;
  onClose: () => void;
  onSaved: () => void;
}

export default function AdminEditProfileModal({ user, onClose, onSaved }: Props) {
  const { showSuccess, showError } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isCoach = user.accountType === 'coach';

  // ── Form state ─────────────────────────────────────────────────────────────

  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  const [displayName, setDisplayName] = useState('');
  const [bio, setBio] = useState('');
  const [experience, setExperience] = useState('');
  const [utr, setUtr] = useState('');
  const [singlePrice, setSinglePrice] = useState('');
  const [specialties, setSpecialties] = useState<CoachSpecialty[]>([]);
  const [specialtyInput, setSpecialtyInput] = useState('');
  const [showSpecialtyDropdown, setShowSpecialtyDropdown] = useState(false);
  const [isAcceptingClients, setIsAcceptingClients] = useState(true);
  const [freeConsultEnabled, setFreeConsultEnabled] = useState(false);
  const [certifications, setCertifications] = useState<string[]>([]);
  const [highestLevel, setHighestLevel] = useState<string[]>([]);
  const [worksWithLevels, setWorksWithLevels] = useState<SkillLevel[]>([]);
  const [previewAvatarUrl, setPreviewAvatarUrl] = useState<string | undefined>(undefined);

  // Populate from the user data passed in
  useEffect(() => {
    if (isCoach) {
      const cp = user.coachProfile;
      setDisplayName(cp?.displayName || user.displayName || '');
      setBio(cp?.bio || '');
      setExperience(cp?.experience?.toString() || '');
      setUtr(cp?.utr?.toString() || '');
      setSpecialties(cp?.specialties || []);
      if (cp?.lessonPrice != null) setSinglePrice((cp.lessonPrice / 100).toFixed(0));
      setIsAcceptingClients(cp?.isAcceptingClients !== false);
      setFreeConsultEnabled(cp?.freeConsultEnabled || false);
      setCertifications(cp?.certifications || []);
      setHighestLevel(cp?.highestLevel || []);
      setWorksWithLevels(cp?.worksWithLevels || []);
      setPreviewAvatarUrl(cp?.avatarUrl || user.avatarUrl || undefined);
    } else {
      const pp = user.playerProfile;
      setDisplayName(pp?.displayName || user.displayName || '');
      setBio(pp?.bio || '');
      setUtr(pp?.utr?.toString() || '');
      setPreviewAvatarUrl(pp?.avatarUrl || user.avatarUrl || undefined);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const trainingPrice = (() => {
    const p = parseFloat(singlePrice);
    return !isNaN(p) && p > 0 ? (p * 4).toFixed(0) : '';
  })();

  const initials = displayName.split(' ').filter(Boolean).map((w) => w[0]).slice(0, 2).join('').toUpperCase() || '?';

  const filteredSpecialties = COACH_SPECIALTIES.filter(
    (s) => !specialties.includes(s) && s.toLowerCase().includes(specialtyInput.toLowerCase())
  );

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { showError('Please select an image file'); return; }
    if (file.size > MAX_IMAGE_SIZE) { showError(`Image must be less than ${getMaxImageSizeLabel()}`); return; }
    try {
      setUploadingAvatar(true);
      if (isCoach) {
        await uploadCoachAvatar(user.uid, file);
      } else {
        await uploadPlayerAvatar(user.uid, file);
      }
      setPreviewAvatarUrl(URL.createObjectURL(file));
      showSuccess('Photo updated');
    } catch {
      showError('Failed to upload photo');
    } finally {
      setUploadingAvatar(false);
      e.target.value = '';
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (isCoach) {
        const data: Record<string, unknown> = {};
        if (displayName) data.displayName = displayName;
        data.bio = bio;
        const exp = parseInt(experience);
        if (!isNaN(exp)) data.experience = exp;
        const u = parseFloat(utr);
        if (!isNaN(u)) data.utr = u;
        const p = parseFloat(singlePrice);
        const lessonPriceCents = !isNaN(p) && p > 0 ? Math.round(p * 100) : null;
        if (lessonPriceCents) data.lessonPrice = lessonPriceCents;
        data.specialties = specialties;
        data.isAcceptingClients = isAcceptingClients;
        data.freeConsultEnabled = freeConsultEnabled;
        data.certifications = certifications;
        data.highestLevel = highestLevel;
        data.worksWithLevels = worksWithLevels;
        await updateCoachProfile(user.uid, data);

        // Sync price into offerings — mirrors Settings.tsx handleSaveCoachSettings
        if (lessonPriceCents) {
          const offerings = await getCoachOfferings(user.uid);
          await Promise.all(offerings.map((o) => {
            if (o.offeringType === 'shot_consult') return Promise.resolve();
            return updateCoachOffering(o.id, { price: lessonPriceCents });
          }));
        }
      } else {
        const data: Record<string, unknown> = {};
        if (displayName) data.displayName = displayName;
        data.bio = bio;
        const u = parseFloat(utr);
        if (!isNaN(u)) data.utr = u;
        await updatePlayerProfile(user.uid, data);
      }
      showSuccess('Profile saved');
      onSaved();
      onClose();
    } catch (err) {
      console.error('[AdminEditProfileModal] save failed:', err);
      showError((err as Error)?.message || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const addSpecialty = (s: CoachSpecialty) => {
    if (!specialties.includes(s)) setSpecialties([...specialties, s]);
    setSpecialtyInput('');
    setShowSpecialtyDropdown(false);
  };

  const removeSpecialty = (s: CoachSpecialty) => {
    setSpecialties(specialties.filter((x) => x !== s));
  };

  const toggleHighestLevel = (level: string) => {
    setHighestLevel((prev) =>
      prev.includes(level) ? prev.filter((l) => l !== level) : [...prev, level]
    );
  };

  const toggleCertification = (cert: string) => {
    setCertifications((prev) =>
      prev.includes(cert) ? prev.filter((c) => c !== cert) : [...prev, cert]
    );
  };

  const toggleWorksWithLevel = (level: SkillLevel) => {
    setWorksWithLevels((prev) =>
      prev.includes(level) ? prev.filter((l) => l !== level) : [...prev, level]
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-xl w-full max-w-2xl shadow-2xl flex flex-col max-h-[90vh]">

        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Edit Profile</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {user.displayName || user.email} · {isCoach ? 'Coach' : 'Player'}
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={saving}
            className="text-gray-400 hover:text-gray-700 disabled:opacity-50"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable form body */}
        <div className="px-6 py-5 space-y-4 overflow-y-auto flex-1 min-h-0">

          {/* Photo */}
          <div>
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Photo</div>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-gray-200 overflow-hidden flex items-center justify-center flex-shrink-0">
                {previewAvatarUrl ? (
                  <img src={previewAvatarUrl} alt={displayName} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-xl font-bold text-gray-500">{initials}</span>
                )}
              </div>
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarUpload}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingAvatar || saving}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition"
                >
                  {uploadingAvatar ? 'Uploading…' : 'Upload photo'}
                </button>
                <div className="text-xs text-gray-400 mt-1">JPG or PNG · at least 400×400px</div>
              </div>
            </div>
          </div>

          <hr className="border-gray-100" />

          {/* About */}
          <div>
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">About</div>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full name</label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-tennis-green input-glow"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
                <RichTextEditor
                  value={bio}
                  onChange={setBio}
                  maxTextLength={MAX_BIO}
                />
              </div>

              {isCoach ? (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Years coaching</label>
                    <input
                      type="number"
                      value={experience}
                      onChange={(e) => setExperience(e.target.value)}
                      min={0} max={50}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-tennis-green input-glow"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Highest UTR</label>
                    <input
                      type="number"
                      value={utr}
                      onChange={(e) => setUtr(e.target.value)}
                      step="0.1" min={1} max={16.5}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-tennis-green input-glow"
                    />
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">UTR rating</label>
                  <input
                    type="number"
                    value={utr}
                    onChange={(e) => setUtr(e.target.value)}
                    step="0.1" min={1} max={16.5}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-tennis-green input-glow"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Coach-only sections */}
          {isCoach && (
            <>
              <hr className="border-gray-100" />

              {/* Coaching credentials */}
              <div>
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Coaching Credentials</div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Playing levels</label>
                    <div className="flex flex-wrap gap-2">
                      {PLAYING_LEVELS.map((level) => (
                        <button
                          key={level}
                          type="button"
                          onClick={() => toggleHighestLevel(level)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                            highestLevel.includes(level)
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                          }`}
                        >
                          {level}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Certifications</label>
                    <div className="flex flex-wrap gap-2">
                      {CERTIFICATIONS.map((cert) => (
                        <button
                          key={cert}
                          type="button"
                          onClick={() => toggleCertification(cert)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                            certifications.includes(cert)
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                          }`}
                        >
                          {cert}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Works with player levels</label>
                    <div className="flex flex-wrap gap-2">
                      {SKILL_LEVELS.map((level) => (
                        <button
                          key={level}
                          type="button"
                          onClick={() => toggleWorksWithLevel(level)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium border capitalize transition ${
                            worksWithLevels.includes(level)
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                          }`}
                        >
                          {level === 'professional' ? 'Semi-Pro' : level}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <hr className="border-gray-100" />

              {/* Lesson pricing */}
              <div>
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Lesson Pricing</div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Shot Consult</label>
                    <div className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-400 font-medium">
                      Free — always
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Single Lesson</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold text-gray-500">$</span>
                      <input
                        type="number"
                        value={singlePrice}
                        onChange={(e) => setSinglePrice(e.target.value)}
                        min={20} max={500}
                        className="w-full pl-6 pr-3 py-2 border border-gray-300 rounded-lg text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-tennis-green input-glow"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Training Program</label>
                    <div className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-400 font-semibold">
                      {trainingPrice ? `$${trainingPrice}` : '—'}
                    </div>
                    <div className="text-xs text-gray-400 mt-0.5">auto: 4×</div>
                  </div>
                </div>
              </div>

              <hr className="border-gray-100" />

              {/* Availability & consults */}
              <div>
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Availability</div>
                <div className="space-y-3">
                  <label className="flex items-center justify-between gap-4 cursor-pointer">
                    <div>
                      <div className="text-sm font-medium text-gray-700">Open for new bookings</div>
                      <div className="text-xs text-gray-400">Players can request lessons</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setIsAcceptingClients(!isAcceptingClients)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        isAcceptingClients ? 'bg-tennis-green' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                          isAcceptingClients ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </label>

                  <label className="flex items-center justify-between gap-4 cursor-pointer">
                    <div>
                      <div className="text-sm font-medium text-gray-700">Free Shot Consults</div>
                      <div className="text-xs text-gray-400">Players get one free consult</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setFreeConsultEnabled(!freeConsultEnabled)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        freeConsultEnabled ? 'bg-tennis-green' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                          freeConsultEnabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </label>
                </div>
              </div>

              <hr className="border-gray-100" />

              {/* Specialties */}
              <div>
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Specialties</div>

                <div className="flex flex-wrap gap-2 mb-2">
                  {specialties.map((s) => (
                    <span
                      key={s}
                      className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-50 border border-blue-200 rounded-full text-xs font-medium text-blue-700"
                    >
                      {s}
                      <button
                        onClick={() => removeSpecialty(s)}
                        className="text-blue-400 hover:text-blue-700 leading-none"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>

                <div className="relative">
                  <input
                    type="text"
                    value={specialtyInput}
                    onChange={(e) => { setSpecialtyInput(e.target.value); setShowSpecialtyDropdown(true); }}
                    onFocus={() => setShowSpecialtyDropdown(true)}
                    onBlur={() => setTimeout(() => setShowSpecialtyDropdown(false), 150)}
                    placeholder="Add a specialty…"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-tennis-green input-glow"
                  />
                  {showSpecialtyDropdown && filteredSpecialties.length > 0 && (
                    <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-10 overflow-hidden max-h-48 overflow-y-auto">
                      {filteredSpecialties.map((s) => (
                        <button
                          key={s}
                          onMouseDown={() => addSpecialty(s)}
                          className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer — always visible */}
        <div className="px-6 py-4 border-t border-gray-200 flex gap-3 justify-end flex-shrink-0">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-semibold disabled:opacity-50 flex items-center gap-2"
          >
            {saving && <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />}
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
